
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";
import { yieldToCandidateWindow } from "./ime";

export const PASSWORD_WINDOW_WIDTH = 250;
export const PASSWORD_WINDOW_HEIGHT = 150;
export const PASSWORD_INPUT_LIMIT = 6;
export const DEFAULT_SAFEBOX_PASSWORD = "000000";

export const PASSWORD_TITLE = "Saving Password";
export const PASSWORD_DESC = [
  "Enter your password",
  "to open the storeroom.",
  "Already changed your password?",
  "If not, your password will be '000000'.",
  "Change your password in storage.",
] as const;

export interface PasswordDialogActions {
  readonly onAccept?: (password: string) => void;
  readonly onCancel?: () => void;
}

const STYLE_ID = "password-dialog-style";
const STYLE = `
.password-dialog {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 80;
  width: ${PASSWORD_WINDOW_WIDTH}px;
  height: ${PASSWORD_WINDOW_HEIGHT}px;
  pointer-events: auto;
}
.password-dialog-title {
  left: 8px;
  width: 234px;
}
.password-dialog-desc {
  position: absolute;
  left: 0;
  width: 100%;
  text-align: center;
  font: 12px/12px Tahoma, "MS Sans Serif", sans-serif;
  color: #d8c7a6;
}
.password-dialog-slot {
  position: absolute;
  left: 50%;
  top: 56px;
  width: 66px;
  height: 24px;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid #6a5a3a;
}
.password-dialog-slot input {
  position: absolute;
  left: 3px;
  top: 3px;
  width: 60px;
  height: 18px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #ead7ae;
  font: 12px/18px Tahoma, "MS Sans Serif", sans-serif;
  letter-spacing: 0.2em;
}
.password-dialog-slot input:focus { outline: none; }
.password-dialog-ok,
.password-dialog-cancel {
  position: absolute;
  top: 120px;
  width: 61px;
  height: 21px;
  border: 0;
  padding: 0;
  color: #ead7ae;
  font: 12px/21px Tahoma, "MS Sans Serif", sans-serif;
  cursor: pointer;
  background: #2a2418;
  border: 1px solid #8d7044;
}
.password-dialog-ok { left: ${250 / 2 - 61 - 5}px; }
.password-dialog-cancel { left: ${250 / 2 + 5}px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "").slice(0, PASSWORD_INPUT_LIMIT);
}

export class PasswordDialog {
  private readonly root: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly actions: PasswordDialogActions;

  constructor(mount: HTMLElement, actions: PasswordDialogActions = {}) {
    this.actions = actions;
    const doc = mount.ownerDocument;
    installChrome(doc);
    installStyle(doc);

    this.root = elem(doc, "div", "password-dialog m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-label", PASSWORD_TITLE);

    const title = elem(doc, "div", "m2-titlebar password-dialog-title", PASSWORD_TITLE);

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.cancel());

    const descTops = [30, 42, 80, 92, 104];
    PASSWORD_DESC.forEach((line, index) => {
      const el = elem(doc, "div", "password-dialog-desc");
      el.style.top = `${descTops[index]}px`;
      el.textContent = line;
      this.root.append(el);
    });

    const slot = elem(doc, "div", "password-dialog-slot");
    this.input = doc.createElement("input");
    this.input.type = "password";
    this.input.maxLength = PASSWORD_INPUT_LIMIT;
    this.input.autocomplete = "off";
    this.input.spellcheck = false;
    this.input.addEventListener("input", () => {
      const next = digitsOnly(this.input.value);
      if (this.input.value !== next) this.input.value = next;
    });
    this.input.addEventListener("keydown", (event) => {
      if (yieldToCandidateWindow(event)) return;
      if (event.key === "Enter") {
        event.preventDefault();
        this.accept();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.cancel();
      }
    });
    slot.append(this.input);

    const ok = doc.createElement("button");
    ok.type = "button";
    ok.className = "password-dialog-ok";
    ok.textContent = "OK";
    ok.addEventListener("click", () => this.accept());

    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.className = "password-dialog-cancel";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.cancel());

    this.root.append(title, close, slot, ok, cancel);
    mount.append(this.root);
    makeDraggable(this.root, title);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }

  get value(): string {
    return this.input.value;
  }

  ask(): void {
    this.input.value = "";
    this.root.hidden = false;
    this.input.focus();
  }

  hide(): void {
    this.root.hidden = true;
    this.input.value = "";
  }

  private accept(): void {
    const password = digitsOnly(this.input.value);
    this.hide();
    this.actions.onAccept?.(password);
  }

  private cancel(): void {
    this.hide();
    this.actions.onCancel?.();
  }
}
